import { useState } from 'react';
import { requestTyped } from '@xdocs/ws-protocol';
import { checkoutStartInput } from '@xdocs/schemas';
import type { CheckoutKind, ErrorAlert } from '@xdocs/schemas';
import { useWs } from '../../../ws/WsProvider.js';

export interface UseCheckoutStartResult {
  start: (kind: CheckoutKind, opts?: { creditPackId?: string; promoCode?: string }) => Promise<void>;
  isStarting: boolean;
  errorMessage: string | undefined;
}

/**
 * F24/F57 checkout launch. `returnUrl` points at this task's dedicated
 * `/checkout/success` and `/checkout/error` routes (task spec) rather than
 * FEATURES.md's literal `/settings/billing?checkout=success|error` —
 * `kind`/`creditPackId` ride along as query params so the return page knows
 * what to poll (`checkout/useCheckoutReturn.ts`). See this task's
 * WIRING.md for the exact contract expected of the Integrate-stage Paddle
 * checkout redirect.
 */
export function useCheckoutStart(): UseCheckoutStartResult {
  const { wsClient } = useWs();
  const [isStarting, setIsStarting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  async function start(kind: CheckoutKind, opts?: { creditPackId?: string; promoCode?: string }): Promise<void> {
    setIsStarting(true);
    setErrorMessage(undefined);
    try {
      const returnBase = `${window.location.origin}/checkout`;
      const params = new URLSearchParams({ kind, ...(opts?.creditPackId ? { creditPackId: opts.creditPackId } : {}) });
      const payload = checkoutStartInput.parse({
        kind,
        ...(opts?.creditPackId ? { creditPackId: opts.creditPackId } : {}),
        ...(opts?.promoCode ? { promoCode: opts.promoCode } : {}),
        returnUrl: `${returnBase}/success?${params.toString()}`,
      });
      const result = await requestTyped(wsClient, 'billing.checkout.start', payload);
      window.location.assign(result.checkoutUrl);
    } catch (err) {
      const alert = err as ErrorAlert;
      setErrorMessage(alert.message ?? 'errors:code.INTERNAL');
      setIsStarting(false);
    }
  }

  return { start, isStarting, errorMessage };
}
